import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { api } from '@/utils/api';

interface Script {
    id: string;
    title: string;
    description?: string;
    lastEditedAt: string;
    organizationId?: string;
}

interface Organization {
    id: string;
    name: string;
}

export function Dashboard() {
    const navigate = useNavigate();
    const [scripts, setScripts] = useState<Script[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<string>('all');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newScript, setNewScript] = useState({ title: '', description: '', organizationId: '' });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, [selectedOrg]);

    const loadData = async () => {
        try {
            const [scriptsData, orgsData] = await Promise.all([
                api.getScripts(selectedOrg !== 'all' ? { organizationId: selectedOrg } : {}),
                api.getOrganizations(),
            ]);
            setScripts(scriptsData);
            setOrganizations(orgsData);
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateScript = async () => {
        try {
            const script = await api.createScript({
                title: newScript.title,
                description: newScript.description || undefined,
                organizationId: newScript.organizationId || undefined,
            });
            setIsCreateModalOpen(false);
            setNewScript({ title: '', description: '', organizationId: '' });
            navigate(`/editor/${script.id}`);
        } catch (error) {
            console.error('Failed to create script:', error);
        }
    };

    const orgOptions = [
        { value: 'all', label: 'All Scripts' },
        { value: 'personal', label: 'Personal Scripts' },
        ...organizations.map(org => ({ value: org.id, label: org.name })),
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header />

            <main style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-2xl)' }}>
                <div className="container">
                    {/* Header Section */}
                    <div className="flex justify-between items-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
                        <div>
                            <h1 style={{ marginBottom: 'var(--spacing-sm)' }}>Dashboard</h1>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 0 }}>
                                Manage your screenplays and collaborate with your team
                            </p>
                        </div>
                        <Button onClick={() => setIsCreateModalOpen(true)} variant="primary">
                            + New Script
                        </Button>
                    </div>

                    {/* Filter Section */}
                    <div style={{ marginBottom: 'var(--spacing-xl)', maxWidth: '300px' }}>
                        <Select
                            value={selectedOrg}
                            onValueChange={setSelectedOrg}
                            options={orgOptions}
                            label="Filter by Organization"
                        />
                    </div>

                    {/* Scripts Grid */}
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 'var(--spacing-2xl)', color: 'var(--color-text-secondary)' }}>
                            Loading...
                        </div>
                    ) : scripts.length === 0 ? (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: 'var(--spacing-2xl)',
                                backgroundColor: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-lg)',
                            }}
                        >
                            <p style={{ fontSize: '1.125rem', marginBottom: 'var(--spacing-lg)' }}>
                                No scripts yet. Create your first screenplay!
                            </p>
                            <Button onClick={() => setIsCreateModalOpen(true)} variant="primary">
                                Create Script
                            </Button>
                        </div>
                    ) : (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: 'var(--spacing-lg)',
                            }}
                        >
                            {scripts.map((script, index) => (
                                <motion.div
                                    key={script.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Card onClick={() => navigate(`/editor/${script.id}`)}>
                                        <h3 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}>
                                            {script.title}
                                        </h3>
                                        {script.description && (
                                            <p
                                                style={{
                                                    color: 'var(--color-text-secondary)',
                                                    fontSize: '0.875rem',
                                                    marginBottom: 'var(--spacing-md)',
                                                }}
                                            >
                                                {script.description}
                                            </p>
                                        )}
                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                                            Last edited: {new Date(script.lastEditedAt).toLocaleDateString()}
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Create Script Modal */}
            <Modal
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                title="Create New Script"
            >
                <div className="flex flex-col gap-lg">
                    <Input
                        label="Title"
                        value={newScript.title}
                        onChange={(e) => setNewScript({ ...newScript, title: e.target.value })}
                        placeholder="Untitled Screenplay"
                    />
                    <div className="flex flex-col gap-sm">
                        <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                            Description (optional)
                        </label>
                        <textarea
                            value={newScript.description}
                            onChange={(e) => setNewScript({ ...newScript, description: e.target.value })}
                            placeholder="Brief description..."
                            rows={3}
                        />
                    </div>
                    {organizations.length > 0 && (
                        <Select
                            value={newScript.organizationId}
                            onValueChange={(value) => setNewScript({ ...newScript, organizationId: value })}
                            options={[
                                { value: '', label: 'Personal Script' },
                                ...organizations.map(org => ({ value: org.id, label: org.name })),
                            ]}
                            label="Organization (optional)"
                            placeholder="Select organization"
                        />
                    )}
                    <div className="flex gap-md" style={{ justifyContent: 'flex-end', marginTop: 'var(--spacing-md)' }}>
                        <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleCreateScript} disabled={!newScript.title}>
                            Create
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
